/**
 * Shared loading and empty state wrappers for chart areas.
 *
 * Replaces the duplicated spinner / "No data" patterns found in
 * BaseInsightLineChart, ConsumptionChart, ForecastChart, and
 * EnterpriseUsageDailyChart.
 */

import type { FC } from "react";

import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Text } from "@/components/flexkit/typography/Text";

type Props = {
    /** Height class applied to the container (e.g. "h-[250px]", "h-[300px] md:h-[400px]"). */
    height?: string;
};

/**
 * Centered spinner with "Loading chart data…" label.
 *
 * ```tsx
 * {loading ? <ChartLoadingState height="h-[250px]" /> : <Chart ... />}
 * ```
 */
export const ChartLoadingState: FC<Props> = ({ height = "h-[250px]" }) => (
    <div className={`flex ${height} items-center justify-center gap-2`} role="status" aria-live="polite">
        <IconSpinner className="animate-spin" size="base" aria-hidden={true} />
        <Text>Loading chart data...</Text>
    </div>
);

type EmptyProps = Props & {
    /** Message shown when there is no data. */
    message?: string;
};

/**
 * Centered empty-state message for charts with no data.
 *
 * ```tsx
 * {data.length === 0 ? <ChartEmptyState /> : <Chart ... />}
 * ```
 */
export const ChartEmptyState: FC<EmptyProps> = ({
    height = "h-[250px]",
    message = "No data available for the selected time range",
}) => (
    <div className={`flex ${height} items-center justify-center`} role="status" aria-live="polite">
        <Text>{message}</Text>
    </div>
);
